import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Table,
  TableColumn,
  Link,
  Breadcrumbs,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  makeStyles,
} from '@material-ui/core';

// ---------------------------------------------------------------------------
// Types (mirror the backend response)
// ---------------------------------------------------------------------------

interface GitlabAccess {
  teamId: string;
  moduleName: string;
  groupName: string;
  maxAccessLevel: string;
  groupSettings: Record<string, string>;
  projectSettings: Record<string, string>;
}

interface AwsPermissionSet {
  name: string;
  targetAccounts: string[];
  allAccounts: boolean;
}

interface AwsAccess {
  groupDisplayName: string;
  permissionSets: AwsPermissionSet[];
}

interface UnionAccess {
  teamId: string;
  policy: string;
}

interface VaultAccessEntry {
  moduleName: string;
  roleName: string;
  paths: string[];
  readOnlyPaths: string[];
  gitlabGroups: string[];
}

interface Team {
  id: string;
  displayName: string;
  objectId?: string;
  sourceFile: string;
}

interface TeamDetails {
  team: Team;
  gitlabAccess: GitlabAccess[];
  awsAccess?: AwsAccess;
  unionAccess: UnionAccess[];
  vaultAccess: VaultAccessEntry[];
  saasApps: string[];
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  breadcrumb: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  noAccess: {
    padding: theme.spacing(4),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
  },
  pathList: {
    maxHeight: 300,
    overflow: 'auto',
    '& .MuiListItem-root': {
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
  monoText: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
  },
}));

// ---------------------------------------------------------------------------
// Sub-components for each access type
// ---------------------------------------------------------------------------

interface GitlabPermRow {
  path: string;
  level: string;
  type: 'group' | 'project';
}

const GitlabAccessCard = ({ access }: { access: GitlabAccess }) => {
  const classes = useStyles();

  const rows: GitlabPermRow[] = [
    ...Object.entries(access.groupSettings).map(([p, l]) => ({
      path: p,
      level: l,
      type: 'group' as const,
    })),
    ...Object.entries(access.projectSettings).map(([p, l]) => ({
      path: p,
      level: l,
      type: 'project' as const,
    })),
  ];

  const columns: TableColumn<GitlabPermRow>[] = [
    {
      title: 'Path',
      field: 'path',
      render: row => <span className={classes.monoText}>{row.path}</span>,
    },
    { title: 'Access Level', field: 'level' },
    {
      title: 'Type',
      field: 'type',
      render: row => (
        <Chip
          label={row.type}
          size="small"
          variant="outlined"
          color={row.type === 'group' ? 'primary' : 'secondary'}
        />
      ),
    },
  ];

  return (
    <InfoCard
      title="GitLab Access"
      subheader={`Group: ${access.groupName} · Max Access Level: ${access.maxAccessLevel}`}
    >
      <Table
        data={rows}
        columns={columns}
        options={{
          search: false,
          paging: rows.length > 10,
          pageSize: 10,
          padding: 'dense',
          toolbar: false,
          header: true,
        }}
      />
    </InfoCard>
  );
};

const AwsAccessCard = ({ access }: { access: AwsAccess }) => {
  const columns: TableColumn<AwsPermissionSet>[] = [
    { title: 'Permission Set', field: 'name' },
    {
      title: 'Target Accounts',
      field: 'targetAccounts',
      render: row =>
        row.allAccounts ? (
          <Chip label="All Active Accounts" size="small" color="primary" />
        ) : (
          row.targetAccounts.join(', ')
        ),
    },
  ];

  return (
    <InfoCard title="AWS SSO Access">
      <Table
        data={access.permissionSets}
        columns={columns}
        options={{
          search: false,
          paging: false,
          padding: 'dense',
          toolbar: false,
        }}
      />
    </InfoCard>
  );
};

const UnionAccessCard = ({ accessList }: { accessList: UnionAccess[] }) => {
  const classes = useStyles();
  return (
    <InfoCard title="Union.AI Access">
      {accessList.map(a => (
        <Chip
          key={a.policy}
          label={`Policy: ${a.policy}`}
          className={classes.chip}
          color="primary"
          variant="outlined"
        />
      ))}
    </InfoCard>
  );
};

const VaultAccessCard = ({ access }: { access: VaultAccessEntry }) => {
  const classes = useStyles();

  return (
    <InfoCard
      title="Vault Access"
      subheader={`Role: ${access.roleName}`}
    >
      <Grid container spacing={2}>
        {access.paths.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Read/Write Paths</Typography>
            <List dense className={classes.pathList}>
              {access.paths.map(p => (
                <ListItem key={p}>
                  <ListItemText
                    primary={<span className={classes.monoText}>{p}</span>}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {access.readOnlyPaths.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Read-Only Paths</Typography>
            <List dense className={classes.pathList}>
              {access.readOnlyPaths.map(p => (
                <ListItem key={p}>
                  <ListItemText
                    primary={<span className={classes.monoText}>{p}</span>}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {access.gitlabGroups.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2">GitLab Groups</Typography>
            {access.gitlabGroups.map(g => (
              <Chip
                key={g}
                label={g}
                size="small"
                className={classes.chip}
                variant="outlined"
              />
            ))}
          </Grid>
        )}
      </Grid>
    </InfoCard>
  );
};

const SaasAppsCard = ({ apps }: { apps: string[] }) => {
  const classes = useStyles();

  return (
    <InfoCard title="SaaS Application Access">
      {apps.map(app => (
        <Chip
          key={app}
          label={app}
          className={classes.chip}
          color="primary"
          variant="outlined"
        />
      ))}
    </InfoCard>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const TeamDetailsPage = () => {
  const classes = useStyles();
  const { teamId } = useParams() as { teamId: string };
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [details, setDetails] = useState<TeamDetails>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('account-access');
      const response = await fetchApi.fetch(`${baseUrl}/teams/${teamId}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Team not found');
        throw new Error(`Failed: ${response.statusText}`);
      }
      setDetails(await response.json());
      setError(undefined);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Loading..." />
        <Content />
      </Page>
    );
  }

  if (error || !details) {
    return (
      <Page themeId="tool">
        <Header title="Team Not Found" />
        <Content>
          <Typography>{error ?? 'Unknown error'}</Typography>
        </Content>
      </Page>
    );
  }

  const { team, gitlabAccess, awsAccess, unionAccess, vaultAccess, saasApps } =
    details;

  const hasAnyAccess =
    gitlabAccess.length > 0 ||
    awsAccess !== undefined ||
    unionAccess.length > 0 ||
    vaultAccess.length > 0 ||
    saasApps.length > 0;

  return (
    <Page themeId="tool">
      <Header
        title={team.displayName}
        subtitle={`Defined in ${team.sourceFile}`}
      />
      <Content>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumb}>
          <Link to="/account-access">All Teams</Link>
          <Typography>{team.displayName}</Typography>
        </Breadcrumbs>

        {!hasAnyAccess && (
          <Typography className={classes.noAccess} variant="h6">
            No access grants found for this team.
          </Typography>
        )}

        <Grid container spacing={3}>
          {gitlabAccess.map(gl => (
            <Grid item xs={12} key={gl.moduleName}>
              <GitlabAccessCard access={gl} />
            </Grid>
          ))}

          {awsAccess && (
            <Grid item xs={12}>
              <AwsAccessCard access={awsAccess} />
            </Grid>
          )}

          {unionAccess.length > 0 && (
            <Grid item xs={12} md={6}>
              <UnionAccessCard accessList={unionAccess} />
            </Grid>
          )}

          {vaultAccess.map(v => (
            <Grid item xs={12} key={v.moduleName}>
              <VaultAccessCard access={v} />
            </Grid>
          ))}

          {saasApps.length > 0 && (
            <Grid item xs={12} md={6}>
              <SaasAppsCard apps={saasApps} />
            </Grid>
          )}
        </Grid>
      </Content>
    </Page>
  );
};
